import classNames from "classnames";
import { useHistory, useParams } from "react-router";
import { blankStyleOverrides, eventPath, stylingOverrideReturns } from "../../utils";
import { ParamsProps } from "../../live-event";
import { LanguagesAbbr, RegistrationStepType, Session, SessionPreview, SessionTypesEnum, } from "../../../../types/working-model";
import React, { useCallback, useMemo, useState } from "react";
import { Tag } from "../../../general-ui/tag/tag";
import { TIconName } from "../../../general-ui/icon/icon.types";
import { useSelector } from "react-redux";
import { EventsState } from "../../../../store/types";
import { Button } from "../../../general-ui/button/button";
import { TemplateClassNames } from "../../../../types/template-layouts";
import { TypographyItem } from "../../../general-ui/typography-item/typography-item";
import { checkIsLive, niceDateTimeTz } from "../../../../utils/utils";
import AddToCalendarModal from "./add-to-calendar-modal/add-to-calendar-modal";
import { useScreenMediaQuery } from "../../../../utils/use-screen-media-query";
import Icon, { ICONS } from "../../../general-ui/icon";
import { useTranslation } from "react-i18next";

interface SessionCardProps {
	session: SessionPreview | Session;
	stylingOverrides?: stylingOverrideReturns;
	template: TemplateClassNames;
	defaultImage: string;
	selectedLanguage?: LanguagesAbbr;
}

const SessionCard: React.FC<SessionCardProps> = ({
	session,
	stylingOverrides = blankStyleOverrides,
	template,
	defaultImage,
	selectedLanguage,
}) => {
	const {
		eventBundle,
		validRegistration,
		registrationId,
		paidSessions
	} = useSelector((event: EventsState) => event.LiveEventReducer);

	const { isLessThan768 } = useScreenMediaQuery();
	const { t } = useTranslation('homepage');

	const [openAddToCalendar, setOpenAddToCalendar] = useState(false);

	const { language, eventName, uuid }: ParamsProps = useParams();
	const history = useHistory();

	const isLive = useMemo(() => {
		return checkIsLive(session);
	}, [session]);

	const isOnDemand = session.session_type === 'on-demand';
	const isBroadcast = session.session_type === SessionTypesEnum.broadcast;

	const {
		moduleDescriptionOverrides,
		moduleCustomHeadingOverrides,
		moduleTitleOverrides,
		secondaryButtonOverrides
	} = stylingOverrides;

	const sessionLink = useMemo(() => {
		const ticketingStep = eventBundle?.registration_steps?.find(step => step.type == RegistrationStepType.ticketing && step.isOn);
		let sessionHasTicket = false;

		if (ticketingStep) {
			const sessionTickets = ticketingStep.ticketing?.filter(ticket => ticket.session_uuids.indexOf(session.uuid) >= 0) || [];

			if (sessionTickets?.length > 0) {
				sessionHasTicket = true;
			}
		}

		if (eventBundle?.registration_on && !validRegistration) {
			let baseUrl = `/${eventPath(eventName, uuid)}/${language}/registration`;

			if (sessionHasTicket) {
				baseUrl = `${baseUrl}/${session.uuid}`;
			}

			return baseUrl;
		} else {
			return `/${eventPath(eventName, uuid)}/${selectedLanguage || language}/session/${session.uuid}`;
		}
	}, [eventBundle, validRegistration, uuid, language, session, eventName, selectedLanguage]);

	const closeAddToCalendar = useCallback(() => {
		setOpenAddToCalendar(false);
	}, []);

	const openAddToCalendarModal = (e: React.SyntheticEvent) => {
		e.stopPropagation();
		setOpenAddToCalendar(true);
	};

	const handleSessionClick = () => {
		if (paidSessions?.length && !paidSessions.includes(session.uuid)) return;
		if (eventBundle?.registration_on && !registrationId) {
			history.push(`/${eventPath(eventName, uuid)}/${selectedLanguage || language}/registration`);
		} else {
			history.push(sessionLink);
		}
	};

	return (
		<div className="agenda-session-card">
			<div className={classNames("agenda-session-card-image", moduleCustomHeadingOverrides)} onClick={handleSessionClick}>
				{session.image ? <img src={session.image} alt={"session image"} /> : <img src={defaultImage} alt={"session image"} />}
				<div className="agenda-session-card-tags-wrapper">
					<Tag
						classNameTag={classNames(moduleCustomHeadingOverrides)}
						text={session?.session_type === "broadcast" ? t("Broadcast") : t("On Demand")}
						icon={"view" as TIconName}
					/>
					{/* live tag stub */}
					{isLive && (
						<Tag
							classNameTag={classNames("tag-container-live", moduleCustomHeadingOverrides)}
							text={t("live")}
						/>
					)}
				</div>
			</div>
			<div className="agenda-session-card-content">
				<div className="agenda-session-card-text-content">
					{/* TODO:  just an example*/}
					{/* <div className="agenda-session-card-label">pre - recorded</div> */}
					<TypographyItem
						className={classNames('agenda-session-card-timestamp', moduleCustomHeadingOverrides, {
							'evt-caption-1': template === TemplateClassNames.Apollo || template === TemplateClassNames["Studio 54"],
							'evt-caption-2 stable': template === TemplateClassNames.O2,
							isOnDemand
						})}
						onClick={handleSessionClick}
					>
						{!isOnDemand && niceDateTimeTz(session.timestamp!)}
					</TypographyItem>
					<TypographyItem
						className={classNames('agenda-session-card-title stable', moduleTitleOverrides, {
							'evt-heading-3': template === TemplateClassNames.Apollo,
							'evt-heading-4': template !== TemplateClassNames.Apollo,
						})}
						tagName="h4"
						onClick={handleSessionClick}
					>
						{session.title[language] || session.title.base}
					</TypographyItem>
					{session.description &&
						<TypographyItem className={classNames('agenda-session-card-description evt-body-text-2 stable', moduleDescriptionOverrides)}>
							{session.description[language] || session.description.base}
						</TypographyItem>
					}
				</div>
				<div className="agenda-session-card-buttons">
					<Button
						classButton={classNames(
							"primary",
							secondaryButtonOverrides)}
						template={template}
						onClick={handleSessionClick}
						isDisabled={!!(paidSessions?.length && !paidSessions.includes(session.uuid))}
					>
						{t("Go to Session")}
					</Button>

					{isBroadcast
						? <Button
							onClick={openAddToCalendarModal}
							classButton={classNames(
								"secondary",
								{ "with-svg": isLessThan768 },
								secondaryButtonOverrides)}
							template={template}
							isDisabled={!!(paidSessions?.length && !paidSessions.includes(session.uuid))}
						>{isLessThan768
							? <Icon name={ICONS.CALENDAR_ADD} color="" size={22} />
							: t("Add to Calendar")
							}</Button>
						: null
					}
				</div>
			</div>
			{
				eventBundle?.uuid ? (
					<AddToCalendarModal
						open={openAddToCalendar}
						close={closeAddToCalendar}
						session={session}
						language={language}
						isSingleSessionNoHomepage={eventBundle?.sessions?.length === 1 && !eventBundle?.homepage}
					/>
				) : null
			}
		</div>
	);
};

export default SessionCard;